import { useMemo } from 'react';

import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { Bonus } from '../../../../types/bonus';
import { UserMode } from '../../../../types/user';
import { isWithinEditWindow } from '../utils/bonus';

export const useContextualizedBonusDetails = (bonus: Bonus | null) => {
	const user = useCurrentUser();
	const canGiveRecognition = usePermission(Permission.CanGiveRecognition);

	return useMemo(() => {
		if (!bonus) {
			return {
				isCreatedByUser: false,
				isEditableByUser: false,
				isGiverHuman: false,
				isInitiatedByUser: false,
				isUserOnlyReceiver: false,
				isUserReceiver: false,
				isUserReceiversManager: false,
				canUserAddOn: false,
				onlyOneReceiver: false,
			};
		}

		const receivers = bonus.receivers ?? [];
		const isUserReceiver = receivers.some(({ id }) => id === user.id);
		const isUserReceiversManager = receivers.some(({ id }) => user.direct_report_ids.includes(id));

		const isInitiatedByUser = bonus?.initiator?.id === user.id;
		const isProxyGiver = bonus?.proxy_giver_id === user.id;
		const isCreatedByUser = isInitiatedByUser || bonus?.giver.id === user.id || isProxyGiver;
		const onlyOneReceiver = receivers.length === 1;
		const isUserOnlyReceiver = isUserReceiver && onlyOneReceiver;
		// On a zero-point program the user has nothing to add to a recognition, so
		// the add-on affordance is indistinguishable from a Comment and just adds
		// dead UI. `monthly_allowance === 0` is the sure-fire signal per
		// jgrahamthomas — `company.zero_point_bonuses` is not reliable in
		// practice. (SUP-118)
		const canUserAddOn = !isUserOnlyReceiver && canGiveRecognition && user.monthly_allowance > 0;
		const isGiverHuman = bonus.giver.user_mode === UserMode.Normal || bonus.giver.user_mode === UserMode.Benefactor;
		const isEditableByUser = isCreatedByUser && bonus.editable && isWithinEditWindow(bonus) && canGiveRecognition;

		return {
			isCreatedByUser,
			isEditableByUser,
			isGiverHuman,
			isInitiatedByUser,
			isUserOnlyReceiver,
			isUserReceiver,
			isUserReceiversManager,
			canUserAddOn,
			onlyOneReceiver,
		};
	}, [bonus, canGiveRecognition, user]);
};
